import { useState } from "react";
import * as Icons from "lucide-react";
import { useEmployeeArchetypes, type EmployeeArchetype } from "@/hooks/useResearchStats";
import { cn } from "@/lib/utils";

function IconFor({ name, className }: { name: string; className?: string }) {
  const Cmp = (Icons as any)[name] || Icons.User;
  return <Cmp className={className} />;
}

export function EmployeeArchetypeWheel({ className }: { className?: string }) {
  const { data: archetypes = [], isLoading } = useEmployeeArchetypes();
  const [active, setActive] = useState<EmployeeArchetype | null>(null);

  if (isLoading) {
    return <div className={cn("h-64 animate-pulse rounded-lg bg-muted", className)} />;
  }
  if (archetypes.length === 0) return null;

  return (
    <div className={cn("space-y-6", className)}>
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
        {archetypes.map((a) => {
          const isActive = active?.id === a.id;
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => setActive(isActive ? null : a)}
              className={cn(
                "group flex flex-col items-center gap-3 rounded-lg border-2 bg-card p-5 text-center transition-all",
                "hover:scale-[1.02] hover:shadow-md",
                isActive ? "shadow-lg" : "border-border",
              )}
              style={isActive ? { borderColor: a.colour_hex } : undefined}
              aria-expanded={isActive}
            >
              <span
                className="flex h-14 w-14 items-center justify-center rounded-full text-white"
                style={{ backgroundColor: a.colour_hex }}
              >
                <IconFor name={a.icon_name} className="h-7 w-7" />
              </span>
              <h3 className="text-base font-semibold text-foreground">{a.label}</h3>
              <p className="text-xs text-muted-foreground">{a.one_liner}</p>
            </button>
          );
        })}
      </div>

      {active && (
        <article
          className="rounded-lg border-l-4 bg-card p-6 animate-in fade-in slide-in-from-top-2"
          style={{ borderLeftColor: active.colour_hex }}
        >
          <header className="mb-3 flex items-center gap-3">
            <span
              className="flex h-10 w-10 items-center justify-center rounded-full text-white"
              style={{ backgroundColor: active.colour_hex }}
            >
              <IconFor name={active.icon_name} className="h-5 w-5" />
            </span>
            <h3 className="text-xl font-bold text-primary">{active.label}</h3>
          </header>
          <p className="text-foreground">{active.description || active.one_liner}</p>
        </article>
      )}
    </div>
  );
}